/**
 * Confirm dialog composable
 * Promise-based confirmation dialog system with shared global state.
 * Usage: const { confirm } = useConfirmDialog()
 *        const ok = await confirm({ title: '...', message: '...' })
 */
import { ref, readonly } from 'vue'

// Shared state
const isOpen = ref(false)
const config = ref({
  title: '',
  message: '',
  confirmText: 'אישור',
  cancelText: 'ביטול',
  variant: 'danger', // 'danger' | 'warning' | 'info'
})

let resolvePromise = null

/**
 * Show a confirmation dialog and return a promise that resolves to true/false
 * @param {Object} options
 * @param {string} options.title - Dialog title
 * @param {string} options.message - Dialog message body
 * @param {string} [options.confirmText='אישור'] - Confirm button text
 * @param {string} [options.cancelText='ביטול'] - Cancel button text
 * @param {string} [options.variant='danger'] - Visual variant: 'danger' | 'warning' | 'info'
 * @returns {Promise<boolean>}
 */
function confirm({
  title,
  message,
  confirmText = 'אישור',
  cancelText = 'ביטול',
  variant = 'danger',
}) {
  // If already open, reject the previous promise
  if (resolvePromise) {
    resolvePromise(false)
  }

  config.value = { title, message, confirmText, cancelText, variant }
  isOpen.value = true

  return new Promise((resolve) => {
    resolvePromise = resolve
  })
}

function handleConfirm() {
  isOpen.value = false
  resolvePromise?.(true)
  resolvePromise = null
}

function handleCancel() {
  isOpen.value = false
  resolvePromise?.(false)
  resolvePromise = null
}

/**
 * Confirm dialog composable hook
 * @returns {Object} Confirm dialog methods and state
 */
export function useConfirmDialog() {
  return {
    isOpen: readonly(isOpen),
    config: readonly(config),
    confirm,
    handleConfirm,
    handleCancel,
  }
}
